import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, ListTodo } from 'lucide-react';
import { DashboardLayout } from '@/layout/DashboardLayout';
import { Button } from '@/design-system';
import { cn } from '@/design-system/utils/cn';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { useMigrationLegacyClientsQuery } from './hooks/useMigrationLegacyClientsQuery';
import { useMigrationLegacyClientQuery } from './hooks/useMigrationLegacyClientQuery';
import { useMigrationMutations } from './hooks/useMigrationMutations';
import {
  LegacyClientsTable,
  type LegacyClientsTableHandlers,
} from './components/LegacyClientsTable';
import { LegacyClientDetailsPanel } from './components/LegacyClientDetailsPanel';
import {
  RunMigrationPanel,
  type RunMigrationPreviewItem,
} from './components/RunMigrationPanel';
import { MigrationJobsPanel } from './components/MigrationJobsPanel';
import type { MigrationLegacyClient } from './types';
import { clampMigrationDays, isPhoneLinkedToV2 } from './utils/migration-format';

const PAGE_LIMIT = 20;

type MigrationTab = 'migrate' | 'progress';

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-white/20 bg-white/15 px-3 py-3 text-white shadow-sm backdrop-blur-sm">
      <div className="text-[11px] font-medium uppercase tracking-wide text-white/75">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
}

export function ConversationMigrationPage() {
  const { runMigration } = useMigrationMutations();
  const onErrorToast = useErrorToast();

  const [tab, setTab] = useState<MigrationTab>('migrate');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [activeClientId, setActiveClientId] = useState<string | null>(null);
  const [selectedPhoneIds, setSelectedPhoneIds] = useState<string[]>([]);
  const [selectedByPhoneId, setSelectedByPhoneId] = useState<
    Record<string, RunMigrationPreviewItem>
  >({});
  const [days, setDays] = useState(7);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const listFilters = useMemo(
    () => ({
      page,
      limit: PAGE_LIMIT,
      search: debouncedSearch || undefined,
    }),
    [page, debouncedSearch]
  );

  const {
    data: listData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useMigrationLegacyClientsQuery(listFilters);

  const legacyClients = useMemo(() => listData?.data ?? [], [listData?.data]);
  const pagination = listData?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 0;

  const {
    data: activeClientDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
  } = useMigrationLegacyClientQuery(activeClientId);

  useEffect(() => {
    if (isError && error) onErrorToast(error, 'Erro ao carregar clientes do legado');
  }, [isError, error, onErrorToast]);

  useEffect(() => {
    if (isDetailError && detailError) {
      onErrorToast(detailError, 'Erro ao carregar detalhes do cliente');
    }
  }, [isDetailError, detailError, onErrorToast]);

  useEffect(() => {
    if (activeClientId === null && legacyClients.length > 0) {
      setActiveClientId(legacyClients[0]?.id ?? null);
    }
  }, [legacyClients, activeClientId]);

  const activeClient = useMemo<MigrationLegacyClient | null>(
    () => activeClientDetail ?? null,
    [activeClientDetail]
  );

  const removeSelection = useCallback((phoneId: string) => {
    setSelectedPhoneIds((current) => current.filter((id) => id !== phoneId));
    setSelectedByPhoneId((current) => {
      if (!(phoneId in current)) return current;
      const next = { ...current };
      delete next[phoneId];
      return next;
    });
  }, []);

  const togglePhoneSelection = useCallback(
    (phoneId: string) => {
      if (!activeClient) return;
      const phone = activeClient.phones.find((p) => p.id === phoneId);
      if (!phone || !isPhoneLinkedToV2(phone)) return;

      setSelectedPhoneIds((current) => {
        if (current.includes(phoneId)) {
          return current.filter((id) => id !== phoneId);
        }
        return [...current, phoneId];
      });
      setSelectedByPhoneId((map) => {
        if (map[phoneId]) {
          const next = { ...map };
          delete next[phoneId];
          return next;
        }
        return { ...map, [phoneId]: { client: activeClient, phone } };
      });
    },
    [activeClient]
  );

  const selectAllPhonesFromActiveClient = useCallback(() => {
    if (!activeClient) return;
    const linked = activeClient.phones.filter(isPhoneLinkedToV2);
    setSelectedPhoneIds((current) => {
      const next = new Set(current);
      linked.forEach((phone) => next.add(phone.id));
      return Array.from(next);
    });
    setSelectedByPhoneId((current) => {
      const next = { ...current };
      linked.forEach((phone) => {
        next[phone.id] = { client: activeClient, phone };
      });
      return next;
    });
  }, [activeClient]);

  const clearActiveClientPhones = useCallback(() => {
    if (!activeClient) return;
    const ids = new Set(activeClient.phones.map((p) => p.id));
    setSelectedPhoneIds((current) => current.filter((id) => !ids.has(id)));
    setSelectedByPhoneId((current) => {
      const next = { ...current };
      for (const id of ids) delete next[id];
      return next;
    });
  }, [activeClient]);

  const selectedPhones = useMemo<RunMigrationPreviewItem[]>(
    () =>
      selectedPhoneIds
        .map((id) => selectedByPhoneId[id])
        .filter((item): item is RunMigrationPreviewItem => Boolean(item)),
    [selectedPhoneIds, selectedByPhoneId]
  );

  const tableHandlers = useMemo<LegacyClientsTableHandlers>(
    () => ({
      onSelectClient: (clientId) => setActiveClientId(clientId),
    }),
    []
  );

  const handleDaysChange = useCallback((value: number) => {
    setDays(clampMigrationDays(value));
  }, []);

  const handleRunMigration = useCallback(async () => {
    try {
      await runMigration.mutateAsync({
        phones: selectedPhones.map(({ client, phone }) => ({
          partnerId: client.id,
          phoneNumberId: phone.id,
        })),
        days: clampMigrationDays(days),
      });
      setTab('progress');
    } catch {
      // toast handled in the mutation hook
    }
  }, [runMigration, selectedPhones, days]);

  return (
    <DashboardLayout title="Migração Orya 1.0">
      <div className="space-y-6">
        <section className="rounded-2xl bg-gradient-to-br from-[#0084FF] to-[#005bb5] px-6 py-6 text-white shadow-lg">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium uppercase tracking-wide text-white/90">
                <ArrowRightLeft size={14} />
                UI de migração
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  Clientes do Orya 1.0
                </h2>
                <p className="mt-2 text-sm leading-6 text-white/90 sm:text-base">
                  Selecione telefones conectados no v2 para migrar o histórico, e acompanhe
                  o status dos jobs por canal.
                </p>
              </div>
            </div>
            {tab === 'migrate' ? (
              <div className="grid grid-cols-2 gap-3 sm:min-w-[280px]">
                <MetricCard label="Clientes" value={total} />
                <MetricCard label="Telefones marcados" value={selectedPhoneIds.length} />
              </div>
            ) : null}
          </div>
        </section>

        <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
          <Button variant="ghost" size="sm"
            type="button"
            onClick={() => setTab('migrate')}
            aria-pressed={tab === 'migrate'}
            className={cn(
              'items-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors',
              tab === 'migrate'
                ? 'bg-[#0084FF] text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            )}
          >
            <ArrowRightLeft size={16} />
            Migrar
          </Button>
          <Button variant="ghost" size="sm"
            type="button"
            onClick={() => setTab('progress')}
            aria-pressed={tab === 'progress'}
            className={cn(
              'items-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors',
              tab === 'progress'
                ? 'bg-[#0084FF] text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            )}
          >
            <ListTodo size={16} />
            Progresso
          </Button>
        </div>

        {tab === 'migrate' ? (
          <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
            <LegacyClientsTable
              clients={legacyClients}
              search={search}
              onSearchChange={setSearch}
              activeClientId={activeClientId}
              loading={isLoading}
              fetching={isFetching && !isLoading}
              page={page}
              totalPages={totalPages}
              total={total}
              limit={PAGE_LIMIT}
              onPageChange={setPage}
              handlers={tableHandlers}
            />

            <div className="space-y-6">
              <LegacyClientDetailsPanel
                client={activeClient}
                loading={Boolean(activeClientId) && isDetailLoading}
                selectedPhoneIds={selectedPhoneIds}
                onTogglePhone={togglePhoneSelection}
                onSelectAllPhones={selectAllPhonesFromActiveClient}
                onClearActiveClientPhones={clearActiveClientPhones}
              />

              <RunMigrationPanel
                isSubmitting={runMigration.isPending}
                onRun={handleRunMigration}
                selectedPhoneCount={selectedPhoneIds.length}
                selectedItems={selectedPhones}
                days={days}
                onDaysChange={handleDaysChange}
                onRemovePhone={removeSelection}
              />
            </div>
          </div>
        ) : (
          <MigrationJobsPanel />
        )}
      </div>
    </DashboardLayout>
  );
}
